import * as React from "react";
import { clsx } from "clsx";
import { useToggle } from "react-use";
import {
  useCreateBusinessReportOutcomeNoteMutation,
  useUpdateBusinessReportOutcomeResultMutation,
} from "@/modules/snapshots/businessReports.api";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { handleNoteError } from "@/modules/snapshots/components/noteErrors";
import { SNAPSHOT_GRID_DATA_COLUMN_WIDTH, getSnapshotGridColumnStyle } from "./layout";

interface Props {
  businessReportOutcome: BusinessReportOutcome;
  columnName: string;
  onRowClick?: () => void;
  isHighlighted?: boolean;
  columnWidth?: number;
  interactionDisabled?: boolean;
}

const MAX_LENGTH = 40;

export const SnapshotGridTableRowNotes: React.FC<Props> = ({
  businessReportOutcome,
  columnName,
  onRowClick,
  isHighlighted = false,
  columnWidth = SNAPSHOT_GRID_DATA_COLUMN_WIDTH,
  interactionDisabled = false,
}) => {
  const [isEditing, toggleIsEditing] = useToggle(false);
  const [createNote] = useCreateBusinessReportOutcomeNoteMutation();
  const [updateResult] = useUpdateBusinessReportOutcomeResultMutation();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const savingRef = React.useRef(false);

  const note = React.useMemo(
    () => businessReportOutcome.result.find(r => r.is_note && r.name === columnName),
    [businessReportOutcome.result, columnName],
  );

  const [editValue, setEditValue] = React.useState(note?.result ?? "");

  React.useEffect(() => {
    setEditValue(note?.result ?? "");
  }, [note?.result]);

  React.useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  const handleCellClick = () => {
    if (interactionDisabled) return;
    onRowClick?.();
    setEditValue(note?.result ?? "");
    toggleIsEditing(true);
  };

  const handleSave = async () => {
    if (interactionDisabled) return;
    if (savingRef.current) return;
    savingRef.current = true;
    const trimmed = editValue.trim();

    if (!trimmed || trimmed === note?.result) {
      setEditValue(note?.result ?? "");
      toggleIsEditing(false);
      savingRef.current = false;
      return;
    }

    try {
      if (note) {
        await updateResult({
          id: businessReportOutcome.id,
          resultId: note.id,
          data: { result: trimmed },
        }).unwrap();
      } else {
        await createNote({
          outcomeId: businessReportOutcome.id,
          content: trimmed,
          name: columnName,
        }).unwrap();
      }
      toggleIsEditing(false);
    } catch (error) {
      handleNoteError(error, note ? "Failed to save note" : "Failed to add note");
    } finally {
      savingRef.current = false;
    }
  };

  React.useEffect(() => {
    if (!interactionDisabled || !isEditing) return;
    setEditValue(note?.result ?? "");
    toggleIsEditing(false);
  }, [interactionDisabled, isEditing, note?.result, toggleIsEditing]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setEditValue(note?.result ?? "");
      toggleIsEditing(false);
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  if (isEditing) {
    return (
      <td
        className={clsx("overflow-hidden p-0 text-sm transition-colors", isHighlighted && "bg-blue-100")}
        style={getSnapshotGridColumnStyle(columnWidth)}
      >
        <div className="px-2 py-1">
          <textarea
            ref={textareaRef}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="w-full resize-none rounded border border-gray-300 px-2 py-1 text-sm focus:border-coral focus:outline-none"
            rows={2}
            placeholder="Add a note..."
          />
        </div>
      </td>
    );
  }

  return (
    <td
      className={clsx(
        "overflow-hidden p-0 text-sm transition-colors group-hover:bg-ivory-mist-400",
        interactionDisabled ? "cursor-default" : "cursor-pointer",
        isHighlighted && "bg-blue-100",
      )}
      style={getSnapshotGridColumnStyle(columnWidth)}
      valign="middle"
      onClick={handleCellClick}
    >
      <div className="min-w-0 overflow-hidden py-1 pl-4 pr-2">
        {note ? (
          <div className="truncate text-xs text-gray-700">
            {note.result.length > MAX_LENGTH ? note.result.substring(0, MAX_LENGTH) + "..." : note.result}
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </div>
    </td>
  );
};
